import { useState, useMemo, useCallback } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  pointerWithin,
  rectIntersection,
  type DragStartEvent,
  type DragEndEvent,
  type CollisionDetection,
} from '@dnd-kit/core';
import { Search, X } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { useDebounce } from '@/hooks/useDebounce';
import { ProjectKanbanColumn } from './ProjectKanbanColumn';
import { ProjectKanbanCard } from './ProjectKanbanCard';
import { UNIVERSAL_STAGES } from '../types';
import type { ProjectsKanbanData, ProjectKanbanCard as CardType, KanbanColumn } from '../types';

interface ProjectsKanbanBoardProps {
  data: ProjectsKanbanData;
  onAdvanceStage: (projectId: string, targetStage: string) => void;
  onUpdatePositions: (updates: Array<{ id: string; position: number }>) => void;
  onProjectClick: (project: CardType) => void;
  onCreateInvoice?: (project: CardType) => void;
}

/**
 * Decide whether a cross-column drop is a permitted stage move.
 * Moves are allowed in BOTH directions across any number of stages; only
 * unknown stages and drops onto the same stage are blocked.
 *
 * Backward moves were relaxed 2026-07-08 (per Hugo): a card advanced by mistake
 * could never be corrected. The server rule (QuotesV4DAO.advanceOperationsStage)
 * was relaxed in the same change and reopens the milestones of the stages being
 * backed out of.
 */
export function resolveStageAdvance(
  sourceStage: string,
  targetStage: string,
  stageKeys: string[],
): { allowed: boolean; reason?: string } {
  const sourceIndex = stageKeys.indexOf(sourceStage);
  const targetIndex = stageKeys.indexOf(targetStage);

  if (sourceIndex < 0 || targetIndex < 0) {
    return { allowed: false, reason: 'Unknown stage' };
  }
  if (targetIndex === sourceIndex) {
    return { allowed: false, reason: 'The card is already in this stage' };
  }
  return { allowed: true };
}

export function ProjectsKanbanBoard({
  data,
  onAdvanceStage,
  onUpdatePositions,
  onProjectClick,
  onCreateInvoice,
}: ProjectsKanbanBoardProps) {
  const [activeProjectId, setActiveProjectId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);

  const filteredData = useMemo(() => {
    const query = debouncedSearch.toLowerCase().trim();
    if (!query) return data;

    const filteredColumns: KanbanColumn[] = data.columns.map((col) => {
      const filtered = col.projects.filter((p) => {
        return (
          (p.quoteNumber && p.quoteNumber.toLowerCase().includes(query)) ||
          (p.quoteTitle && p.quoteTitle.toLowerCase().includes(query)) ||
          (p.accountName && p.accountName.toLowerCase().includes(query)) ||
          (p.projectNumber && p.projectNumber.toLowerCase().includes(query))
        );
      });
      return {
        ...col,
        projects: filtered,
        totals: {
          projectCount: filtered.length,
          sensorsQty: filtered.reduce((sum, p) => sum + p.sensorsQty, 0),
          value: filtered.reduce((sum, p) => sum + p.amount, 0),
        },
      };
    });

    return {
      ...data,
      columns: filteredColumns,
      totalCount: filteredColumns.reduce((sum, col) => sum + col.projects.length, 0),
    };
  }, [data, debouncedSearch]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );

  const collisionDetection: CollisionDetection = useMemo(
    () => (args) => {
      const pointerCollisions = pointerWithin(args);
      if (pointerCollisions.length > 0) return pointerCollisions;
      return rectIntersection(args);
    },
    [],
  );

  const findProjectColumn = useCallback(
    (projectId: string) => data.columns.find((col) => col.projects.some((p) => p.id === projectId)),
    [data.columns],
  );

  const activeProject = useMemo(() => {
    if (!activeProjectId) return null;
    for (const col of data.columns) {
      const found = col.projects.find((p) => p.id === activeProjectId);
      if (found) return found;
    }
    return null;
  }, [activeProjectId, data.columns]);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveProjectId(event.active.id as string);
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setActiveProjectId(null);
      const { active, over } = event;
      if (!over || !active) return;

      const activeId = active.id as string;
      const overId = over.id as string;

      const sourceColumn = findProjectColumn(activeId);
      if (!sourceColumn) return;

      const stageKeys = UNIVERSAL_STAGES.map((s) => s.key) as string[];
      const isOverColumn = stageKeys.includes(overId);
      const targetStage = isOverColumn ? overId : findProjectColumn(overId)?.stage;

      if (!targetStage) return;

      // Cross-column: move stage (either direction; any multi-stage jump allowed)
      if (targetStage !== sourceColumn.stage) {
        const { allowed, reason } = resolveStageAdvance(sourceColumn.stage, targetStage, stageKeys);
        if (!allowed) {
          toast.error(reason ?? 'That move is not allowed');
          return;
        }

        onAdvanceStage(activeId, targetStage);
        return;
      }

      // Within-column: reorder by position
      const targetColumn = data.columns.find((c) => c.stage === targetStage);
      if (!targetColumn) return;

      if (isOverColumn) {
        // Dropped on column header — move to end
        const maxPos = targetColumn.projects.reduce((max, p) => Math.max(max, p.position), 0);
        onUpdatePositions([{ id: activeId, position: maxPos + 1000 }]);
      } else {
        // Dropped on another card — insert before it
        const overProject = targetColumn.projects.find((p) => p.id === overId);
        if (!overProject || overProject.id === activeId) return;

        const overIndex = targetColumn.projects.indexOf(overProject);
        const prevPosition = overIndex > 0 ? targetColumn.projects[overIndex - 1].position : 0;
        const newPosition = Math.floor((prevPosition + overProject.position) / 2);

        onUpdatePositions([{ id: activeId, position: newPosition || overProject.position - 500 }]);
      }
    },
    [data.columns, findProjectColumn, onAdvanceStage, onUpdatePositions],
  );

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetection}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="mb-4 relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search by quote #, title, account name or number..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9 pr-9"
        />
        {search && (
          <button
            onClick={() => setSearch('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      <div className="flex gap-4 overflow-x-auto pb-4" style={{ minHeight: 'calc(100vh - 250px)' }}>
        {filteredData.columns.map((column) => (
          <ProjectKanbanColumn
            key={column.stage}
            column={column}
            activeProjectId={activeProjectId}
            onProjectClick={onProjectClick}
            onCreateInvoice={onCreateInvoice}
          />
        ))}
      </div>

      <DragOverlay>
        {activeProject ? (
          <div className="w-[264px]">
            <ProjectKanbanCard project={activeProject} isDragging />
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
